// @ts-check

const STORED_PREFERENCE_COOKIE_DATA_DELIMITER = ',';
const COOKIE_DELETION_DELAY_MS = 1000;

const LOCALHOST = 'localhost';
/**
 * @callback OnConsentChangedCallback
 * @param {Event & { details: Array<string> }} event
 */

/**
 * @callback GetDomainData
 * @returns {OneTrustDomainData}
 */

/**
 * @typedef {Object} OneTrustCookieData
 * @property {number} DurationType - The duration type of the cookie.
 * @property {string} Host - The host of the cookie.
 * @property {boolean} IsSession - Indicates if the cookie is a session cookie.
 * @property {string} Length - The length of the cookie.
 * @property {string} Name - The name of the cookie.
 * @property {*} category - The category of the cookie. (Type left as 'any')
 * @property {string} description - The description of the cookie.
 * @property {string} firstPartyKey - The first-party key of the cookie.
 * @property {string} id - The ID of the cookie.
 * @property {boolean} isThirdParty - Indicates if the cookie is a third-party cookie.
 * @property {string} patternKey - The pattern key of the cookie.
 * @property {string} thirdPartyDescription - The third-party description of the cookie.
 * @property {string} thirdPartyKey - The third-party key of the cookie.
 */

/**
 * @typedef {Object} OneTrustGroupData
 * @property {Array<OneTrustCookieData>} Cookies - The collection of cookies in the category of CustomGroupId.
 * @property {("C0001" | "C0002" | "C0003" | "C0004" | "C0005" | string)} CustomGroupId - The ID of the custom group.
 */

/**
 * @typedef {Object} OneTrustDomainData
 * @property {Array<OneTrustGroupData>} Groups - The array of group data.
 */

/**
 * @typedef {Object} CookieService
 * @property {Function} get
 * @property {Function} getObject
 * @property {Function} getAll
 * @property {Function} put
 * @property {Function} putObject
 * @property {Function} remove
 */

/**
 * if you are looking for more information on the API itself visit
 * https://developer.onetrust.com/onetrust/docs/javascript-api - You will need an account that you can create yourself.
 * CMA has more details on the API <TODO Add Link to TypeDef>
 * @typedef {Object} OneTrustApi
 * @property {GetDomainData} GetDomainData - Get domain data.
 * @property {OnConsentChangedCallback} OnConsentChanged - Register a callback for consent changes.
 */

export default class OneTrustConsentService {

    /* @ngInject */
    /**
     * 
     * @param {Window & { OneTrust: OneTrustApi, OnetrustActiveGroups: string}} $window
     * @param {CookieService} $cookies
     */
    constructor($window, $cookies) {
        this.$window = $window;
        this.$cookies = $cookies;
    }

    /**
    * @description Initializes the OneTrust user consent.
    * @returns {void}
    */
    initialize() {
        const checkOneTrustReady = () => new Promise((resolve) => {
            if (this.$window.OneTrust) {
                const previousConsent = this.$window.OnetrustActiveGroups;

                this.$window.OneTrust.OnConsentChanged(this.handleConsentPreference.bind(this));
                if (!!previousConsent) {
                    const acceptedCategories = previousConsent
                        .split(STORED_PREFERENCE_COOKIE_DATA_DELIMITER)
                        .filter(Boolean);

                        // Settings a timeout in order to make sure all cookies are created, since this can kick-in fairly early
                    setTimeout(
                        () => this.deleteRejectedCookiesInCategory(acceptedCategories),
                        COOKIE_DELETION_DELAY_MS
                    );
                }

              resolve(undefined);
            } else {
              setTimeout(checkOneTrustReady, 100);
            }
        });

        checkOneTrustReady();
    }

  /**
   * @description Deletes rejected cookies based on the specified categories.
   *
   * Goes through all of the consent categories and delete the cookies where the user
   * didn't give consent for the cookies to be used. The categorization of cookies is managed in OneTrust.
   * @param {string[]} acceptedCategories - The categories of rejected cookies.
   * @returns {void}
   */
    deleteRejectedCookiesInCategory(acceptedCategories) {
        const domainData = this.$window.OneTrust.GetDomainData();
        const cookies = this.$cookies.getAll();
        const rootDomain = this.getRootDomain();

        /**
         *
         * @param {OneTrustGroupData} group - The group of data for a specific category
         */
        const deleteCookies = (group) => {
            group.Cookies.forEach(cookieInGroup => {
                const cookieNames = cookieInGroup.patternKey != undefined
                    ? Object.keys(cookies).filter(c => c.startsWith(cookieInGroup.patternKey))
                    : [cookieInGroup.Name];

                this.deleteCookie(cookieNames, rootDomain);
            });
        };

        domainData.Groups
            .filter(group => !acceptedCategories.includes(group.CustomGroupId))
            .forEach(group => deleteCookies(group));
    }

    /**
     * 
     * @param {string[]} cookiesName 
     * @param {string} rootDomain 
     * @returns {void}
     */
    deleteCookie(cookiesName, rootDomain) {
        if (cookiesName.length == 0) {
          return;
        }

        cookiesName.forEach(cookie => {
          this.$cookies.remove(cookie, '/', this.$window.location.hostname);

          /**
           * Per the [RFC](https://datatracker.ietf.org/doc/html/rfc6265#section-3.1) if you want to delete a cookie
           * you need to provide the domain. In a localhost environment we have all the right to delete cookies.
           * But once we are under a real domain we need to provide a domain. We check for the main domain delete.
           *
           * Just to be safe we call delete twice if the cookie was registered under both domains.
           */
          if (this.$window.location.hostname != rootDomain) {
            this.$cookies.remove(cookie, '/', `.${rootDomain}`);
          }
        });
    }

    /**
     * Return the root domain of the current location.
     *
     * @example
     * domain = 'app.bluecrew.com'
     * returns = 'bluecrew.com'
     *
     * @returns {string}
     */
    getRootDomain() {
        if (this.$window.location.hostname === LOCALHOST) {
          return LOCALHOST;
        }

        const parts = this.$window.location.hostname.split('.');
        const tld = parts[parts.length - 1];
        const sld = parts[parts.length - 2];

        return `${sld}.${tld}`;
      }

    /**
    * @description Handles the user's consent preference.
    * @param {Event & { detail: Array<string> }} event - The consent preference event.
    * @returns {void}
    * @private
    */
    handleConsentPreference(event) {
        this.deleteRejectedCookiesInCategory(event.detail);
    }
}
